import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, type FieldOption } from '@noor/ui';
import { MEASUREMENT_TYPES } from '../config/unit-codes';

/**
 * How the product's weight is shown to customers and in the merchant app.
 *
 * Deliberately separate from the OFD `unitCode` select above it: that one is a
 * tax classifier read only by the fiscal receipt, while this one is purely
 * presentational (`measurement_type` on the wire).
 */
export function MeasurementTypeField({
  className,
  fieldClassName,
}: {
  className?: string;
  fieldClassName?: string;
}) {
  const { t } = useTranslation('eats');
  const options = useMemo<FieldOption[]>(
    () => [
      { value: 'none', label: t('menu.product.form.measurementTypes.none') },
      ...MEASUREMENT_TYPES.map(({ code, labelKey }) => ({
        value: code,
        label: t(`menu.product.form.units.${labelKey}`),
      })),
    ],
    [t],
  );

  return (
    <FormField name="measurementType" {...(className ? { className } : {})}>
      <FormField.Label className="flex items-center gap-0.5 text-sm font-medium tracking-tight">
        {t('menu.product.form.measurementTypeLabel')}
      </FormField.Label>
      <FormField.Select
        {...(fieldClassName ? { className: fieldClassName } : {})}
        options={options}
        placeholder={t('menu.product.form.measurementTypePlaceholder')}
      />
      <span className="text-xs leading-relaxed text-muted-foreground">
        {t('menu.product.form.measurementTypeHint')}
      </span>
      <FormField.Message />
    </FormField>
  );
}
